import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { deptLabel } from '../workflow/VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_DRAFTS = 20;

// Patient search, check-in, claim items, drafts and claim actions.
export default function useClaimWorkspace({
  axiosConfig, isSuperAdmin, hospitalName, hospitalCurrency,
  formatCurrency, toLocalBalance, loadClaims, loadHospitalStats, loadHospitalBalance,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyInfo, setFamilyInfo] = useState(null);
  const [searchType, setSearchType] = useState(''); // 'individual' or 'family'
  const [familyClaimHistory, setFamilyClaimHistory] = useState([]);
  const [selectedClaimDetails, setSelectedClaimDetails] = useState(null);
  const [showClaimDetailsModal, setShowClaimDetailsModal] = useState(false);
  const [claimItems, setClaimItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [patientBypassStates, setPatientBypassStates] = useState({});
  const [claimDrafts, setClaimDrafts] = useState([]);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // ==================== COOLDOWN BYPASS ====================

  const loadPatientBypassStatus = async (serialNumber) => {
    try {
      const response = await axios.get(`${API}/settings/patient-cooldown-bypass/${serialNumber}`, axiosConfig);
      setPatientBypassStates(prev => ({ ...prev, [serialNumber]: response.data.bypass_enabled }));
      return response.data.bypass_enabled;
    } catch (error) {
      console.error('Failed to load patient bypass status:', error);
      return false;
    }
  };

  const togglePatientBypass = async (serialNumber) => {
    try {
      const response = await axios.post(`${API}/settings/patient-cooldown-bypass/${serialNumber}`, {}, axiosConfig);
      setPatientBypassStates(prev => ({ ...prev, [serialNumber]: response.data.bypass_enabled }));
      toast.success(response.data.message);
      return response.data.bypass_enabled;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle bypass');
      return null;
    }
  };

  const loadFamilyMembersBypassStatus = async (members) => {
    await Promise.all(members.map(member => loadPatientBypassStatus(member.serial_number)));
  };

  // ==================== SEARCH & CHECK-IN ====================

  const doCheckIn = async (patient, reason, overrideCooldown = false, vitals = null, departments = ['general'], client = {}) => {
    try {
      const res = await axios.post(`${API}/visits/checkin`, {
        patient_serial_number: patient.serial_number,
        reason: reason || '',
        override_cooldown: overrideCooldown,
        vitals: vitals ? Object.fromEntries(Object.entries(vitals).filter(([, v]) => v && v.trim())) : null,
        departments,
        client_location: client.location || '',
        client_phone: client.phone || '',
        preauth_code: client.preauth_code || '',
        specialist: client.specialist || null
      }, axiosConfig);
      const deptNames = (res.data.departments || departments).map(deptLabel).join(' → ');
      const statusMsg = res.data.status === 'AWAITING_PREAUTH'
        ? `${res.data.patient_name} checked in (${res.data.visit_id}) — sent for pre-authorization approval by the administrator.`
        : `${res.data.patient_name} checked in (${res.data.visit_id}) — sent to: ${deptNames}.`;
      toast.success(statusMsg, { duration: 6000 });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Check-in failed';
      if (error.response?.status === 403 && isSuperAdmin && /cooldown|treatment interval/i.test(detail) && !overrideCooldown) {
        setConfirmDialog({
          title: '14-day treatment interval active',
          description: `${detail}\n\nOverride the treatment interval and check in anyway?`,
          confirmLabel: 'Override & Check In',
          destructive: true,
          onConfirm: () => doCheckIn(patient, reason, true, vitals, departments, client),
        });
        return;
      }
      toast.error(detail, { duration: 6000 });
    }
  };

  const handleCheckIn = async (patient) => {
    let preauthRequired = false;
    try {
      const r = await axios.get(`${API}/hospital/workflow-status`, axiosConfig);
      preauthRequired = !!r.data.preauth_required;
    } catch (e) { /* default: no specialist dropdown */ }
    setConfirmDialog({
      preauthRequired,
      title: `Check In — ${patient.first_name} ${patient.last_name}`,
      withReason: true,
      withVitals: true,
      withDepartments: true,
      initialPhone: patient.phone || '',
      reasonLabel: 'Reason for visit (optional)',
      reasonPlaceholder: 'e.g. Fever and headache',
      confirmLabel: 'Check In',
      onConfirm: (reason, vitals, departments, client) => doCheckIn(patient, reason, false, vitals, departments, client),
    });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/patients/search?query=${searchQuery}`, axiosConfig);
      const data = response.data;

      if (data.type === 'family') {
        if (!data.family) {
          toast.error('No family found');
          setSelectedPatient(null);
          setFamilyMembers([]);
          setFamilyInfo(null);
          setSearchType('');
          setFamilyClaimHistory([]);
        } else {
          setFamilyInfo(data.family);
          setFamilyMembers(data.members);
          setSelectedPatient(null);
          setSearchType('family');
          toast.success(`Found family ${data.family.family_id} with ${data.members.length} members`);
          loadFamilyClaimHistory(data.family.family_id);
          loadFamilyMembersBypassStatus(data.members);
        }
      } else {
        if (data.results.length === 0) {
          toast.error('No patients found');
          setSelectedPatient(null);
          setFamilyMembers([]);
          setFamilyInfo(null);
          setSearchType('');
          setFamilyClaimHistory([]);
        } else {
          setSelectedPatient(data.results[0]);
          setFamilyMembers([]);
          setFamilyInfo(null);
          setSearchType('individual');
          if (data.results.length === 1) {
            toast.success('Patient found');
          } else {
            toast.info(`Found ${data.results.length} patients, showing first result`);
          }
          loadFamilyClaimHistory(data.results[0].family_id);
          loadPatientBypassStatus(data.results[0].serial_number);
        }
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyClaimHistory = async (family_id) => {
    try {
      const response = await axios.get(`${API}/claims/family/${family_id}`, axiosConfig);
      setFamilyClaimHistory(response.data.claims || []);
    } catch (error) {
      console.error('Failed to load claim history:', error);
      setFamilyClaimHistory([]);
    }
  };

  const handleViewClaimDetails = (claim) => {
    setSelectedClaimDetails(claim);
    setShowClaimDetailsModal(true);
  };

  const handleCreateClaimForMember = (member) => {
    setSelectedPatient({ ...member, remaining_balance: familyInfo.remaining_balance });
    setSearchType('individual');
  };

  // ==================== CLAIM ITEMS ====================

  const handleRemoveItem = (index) => {
    setClaimItems(claimItems.filter((_, i) => i !== index));
    toast.info('Item removed');
  };

  const getTotalClaimCost = () => claimItems.reduce((sum, item) => sum + (item.cost * (item.quantity || 1)), 0);
  const getTotalClaimCostUSD = () => getTotalClaimCost() / (hospitalCurrency.rate_to_usd || 1);

  // ==================== CLAIM DRAFTS ====================

  useEffect(() => {
    try {
      const savedDrafts = localStorage.getItem(`claimDrafts_${hospitalName}`);
      if (savedDrafts) {
        setClaimDrafts(JSON.parse(savedDrafts));
      }
    } catch (error) {
      console.error('Failed to load drafts:', error);
    }
  }, []);

  const saveClaimDraftsToStorage = (drafts) => {
    try {
      localStorage.setItem(`claimDrafts_${hospitalName}`, JSON.stringify(drafts));
    } catch (error) {
      toast.error('Failed to save drafts');
    }
  };

  const handleSaveDraft = () => {
    if (claimItems.length === 0) {
      toast.error('No items to save as draft');
      return;
    }
    if (!selectedPatient) {
      toast.error('Please select a patient first');
      return;
    }
    if (claimDrafts.length >= MAX_DRAFTS) {
      toast.error(`Maximum ${MAX_DRAFTS} drafts allowed. Please delete some drafts first.`);
      return;
    }

    const newDraft = {
      id: `DRAFT-${Date.now()}`,
      clientName: selectedPatient.name || selectedPatient.patient_name || 'Unknown',
      clientId: selectedPatient.serial_number || '',
      familyId: selectedPatient.family_id || '',
      items: [...claimItems],
      totalAmount: getTotalClaimCost(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updatedDrafts = [...claimDrafts, newDraft];
    setClaimDrafts(updatedDrafts);
    saveClaimDraftsToStorage(updatedDrafts);
    toast.success(`Draft saved for ${newDraft.clientId}`);
  };

  const handleUpdateDraft = (draftId) => {
    if (claimItems.length === 0) {
      toast.error('No items to update draft');
      return;
    }
    const updatedDrafts = claimDrafts.map(draft => (
      draft.id === draftId
        ? { ...draft, items: [...claimItems], totalAmount: getTotalClaimCost(), updatedAt: new Date().toISOString() }
        : draft
    ));
    setClaimDrafts(updatedDrafts);
    saveClaimDraftsToStorage(updatedDrafts);
    setEditingDraft(null);
    toast.success('Draft updated successfully');
  };

  const handleLoadDraft = (draft) => {
    setClaimItems([...draft.items]);
    setShowDraftsModal(false);
    toast.info(`Loaded draft: ${draft.clientName}`);
  };

  const handleDeleteDraft = (draftId) => {
    const updatedDrafts = claimDrafts.filter(d => d.id !== draftId);
    setClaimDrafts(updatedDrafts);
    saveClaimDraftsToStorage(updatedDrafts);
    toast.info('Draft deleted');
  };

  // ==================== CLAIM ACTIONS ====================

  const handleSubmitClaim = async () => {
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }
    if (claimItems.length === 0) {
      toast.error('Please add items to the claim');
      return;
    }

    const totalCost = getTotalClaimCost();
    const totalCostUSD = getTotalClaimCostUSD();
    if (totalCostUSD > selectedPatient.remaining_balance) {
      toast.error(`Insufficient funds! Available: ${formatCurrency(toLocalBalance(selectedPatient.remaining_balance))}, Required: ${formatCurrency(totalCost)}`);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/claims/submit`,
        {
          patient_serial_number: selectedPatient.serial_number,
          claim_items: claimItems.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_cost: item.cost,
            quantity: item.quantity || 1
          })),
          override_cooldown: selectedPatient.override_cooldown || false
        },
        axiosConfig
      );

      toast.success(`Claim created successfully! Claim ID: ${response.data.claim_id}`);
      setClaimItems([]);
      setSelectedPatient({
        ...selectedPatient,
        remaining_balance: response.data.new_balance,
        override_cooldown: false,
        last_claim_date: new Date().toISOString()
      });

      loadClaims().catch(err => console.error('Error reloading claims:', err));
      if (isSuperAdmin) {
        loadHospitalStats().catch(err => console.error('Error reloading hospital stats:', err));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setLoading(false);
    }
  };

  const handleVoidClaim = (claimId) => {
    setConfirmDialog({
      title: 'Void claim',
      description: 'Are you sure you want to void this claim? The amount will be refunded to the family balance.',
      confirmLabel: 'Void Claim',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/claims/${claimId}/void`, {}, axiosConfig);
          toast.success('Claim voided successfully');
          await loadClaims();
          await loadHospitalStats();
          if (selectedPatient) {
            const response = await axios.get(`${API}/patients/${selectedPatient.serial_number}`, axiosConfig);
            setSelectedPatient(response.data);
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to void claim');
        }
      },
    });
  };

  const handleMarkAsPaid = (claimId, claimAmount) => {
    setConfirmDialog({
      title: 'Mark claim as paid',
      description: `This will deduct ${formatCurrency(claimAmount)} from your hospital balance.`,
      confirmLabel: 'Mark as Paid',
      onConfirm: async () => {
        try {
          const response = await axios.post(`${API}/claims/${claimId}/pay`, {}, axiosConfig);
          toast.success(response.data.message);
          await loadClaims();
          await loadHospitalBalance();
          await loadHospitalStats();
          if (selectedPatient) {
            const patientResponse = await axios.get(`${API}/patients/${selectedPatient.serial_number}`, axiosConfig);
            setSelectedPatient(patientResponse.data);
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to mark claim as paid');
        }
      },
    });
  };

  return {
    searchQuery, setSearchQuery, selectedPatient, familyMembers, familyInfo, searchType, familyClaimHistory,
    selectedClaimDetails, showClaimDetailsModal, setShowClaimDetailsModal,
    claimItems, setClaimItems, loading,
    patientBypassStates, togglePatientBypass,
    claimDrafts, showDraftsModal, setShowDraftsModal, editingDraft, setEditingDraft, MAX_DRAFTS,
    confirmDialog, setConfirmDialog,
    handleSearch, handleCheckIn, handleViewClaimDetails, handleCreateClaimForMember,
    handleRemoveItem, getTotalClaimCost, getTotalClaimCostUSD,
    handleSaveDraft, handleUpdateDraft, handleLoadDraft, handleDeleteDraft,
    handleSubmitClaim, handleVoidClaim, handleMarkAsPaid,
  };
}
